import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { BookmarkPlus, X } from 'lucide-react';

const AddToHomeScreen = ({ darkMode }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isHidden, setIsHidden] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    // Check if user has already dismissed this
    const dismissed = localStorage.getItem('emoq_home_screen_added');
    if (dismissed === 'true') {
      setIsHidden(true);
    }

    // Detect iOS
    const userAgent = navigator.userAgent.toLowerCase();
    const iOS = /iphone|ipad|ipod/.test(userAgent);
    setIsIOS(iOS);

    // Listen for the beforeinstallprompt event (Android/Desktop PWA)
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handler);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
    };
  }, []);

  const handleClick = async () => {
    // For Android/Desktop with PWA support - trigger native prompt
    if (deferredPrompt) {
      try {
        await deferredPrompt.prompt();
        const choiceResult = await deferredPrompt.userChoice;
        
        if (choiceResult.outcome === 'accepted') {
          // User accepted - hide button permanently
          localStorage.setItem('emoq_home_screen_added', 'true');
          setIsHidden(true);
        }
        
        setDeferredPrompt(null);
      } catch (error) {
        // If native prompt fails, show instructions
        setIsModalOpen(true);
      }
    } else {
      // For iOS or browsers without PWA support - show instructions
      setIsModalOpen(true);
    }
  };

  const handleGotIt = () => {
    localStorage.setItem('emoq_home_screen_added', 'true');
    setIsHidden(true);
    setIsModalOpen(false);
  };

  const handleClose = () => {
    setIsModalOpen(false);
  };

  // Don't render if user already added
  if (isHidden) {
    return null;
  }

  return (
    <>
      {/* Trigger Button */}
      <Button
        variant="ghost"
        size="sm"
        onClick={handleClick}
        className={`
          w-full md:w-auto flex items-center gap-2 px-3 py-2 rounded-lg transition-all duration-200 transform hover:scale-105 active:scale-95 justify-start
          ${darkMode 
            ? 'hover:bg-gray-700 text-gray-300 hover:text-white hover:shadow-lg hover:shadow-gray-700/50' 
            : 'hover:bg-gray-100 text-gray-700 hover:text-gray-900 hover:shadow-lg hover:shadow-gray-300/50'
          }
        `}
        title="Save to Home Screen"
      >
        <BookmarkPlus className="w-4 h-4" />
        <span className="text-sm font-medium">Quick Access</span>
      </Button>

      {/* Instructions Modal (only for iOS or fallback) */}
      {isModalOpen && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100000] p-4"
          onClick={handleClose}
        >
          <div 
            className={`
              ${darkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'}
              rounded-2xl shadow-2xl max-w-sm w-full p-6 relative
            `}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={handleClose}
              className={`
                absolute top-3 right-3 p-1 rounded-lg transition-colors
                ${darkMode 
                  ? 'hover:bg-gray-700 text-gray-400 hover:text-white' 
                  : 'hover:bg-gray-100 text-gray-500 hover:text-gray-900'
                }
              `}
            >
              <X className="w-5 h-5" />
            </button>

            {/* Content */}
            <div className="text-center">
              <div className="text-5xl mb-4">📱</div>
              <h2 className={`text-xl font-bold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                Save on Phone Screen for Quick Access
              </h2>
              <p className={`text-sm mb-6 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                {isIOS 
                  ? 'Tap Share button (bottom), then "Add to Home Screen"'
                  : 'Tap menu (⋮) at top, then "Add to Home screen"'
                }
              </p>

              {/* Got It Button */}
              <Button
                onClick={handleGotIt}
                className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-bold py-3 rounded-lg"
              >
                Got It!
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AddToHomeScreen;
